// src/utils/performance.js

/**
 * Performance utility functions for optimization and monitoring
 */

/**
 * Throttle function to limit how often a function can be called
 * Great for scroll events, resizing, and other frequent events
 * 
 * @param {Function} func - The function to throttle
 * @param {number} limit - The time limit in milliseconds
 * @returns {Function} - Throttled function
 */
export const throttle = (func, limit) => {
  let inThrottle;
  let lastResult;
  
  return function(...args) {
    if (!inThrottle) {
      lastResult = func.apply(this, args);
      inThrottle = true;
      
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
    
    return lastResult;
  };
};

/**
 * Debounce function for operations that should wait for a pause
 * Great for search inputs and similar user interactions
 * 
 * @param {Function} func - The function to debounce
 * @param {number} delay - The delay in milliseconds
 * @returns {Function} - Debounced function
 */
export const debounce = (func, delay) => {
  let timeout;
  
  return function(...args) {
    const context = this;
    clearTimeout(timeout);
    
    timeout = setTimeout(() => {
      func.apply(context, args);
    }, delay);
  };
};

/**
 * Initialize performance monitoring for Core Web Vitals
 * Records LCP, FID, and CLS metrics
 * 
 * @param {Function} reportCallback - Optional callback for reporting metrics
 */
export const initPerformanceMonitoring = (reportCallback) => {
  // Only run in browser environment
  if (typeof window === 'undefined' || !window.PerformanceObserver) return;
  
  // Store metrics
  const metrics = {
    lcp: null, // Largest Contentful Paint
    fid: null, // First Input Delay
    cls: 0,    // Cumulative Layout Shift
  };
  
  // Report metrics to analytics or console
  const reportMetrics = () => {
    if (typeof reportCallback === 'function') {
      reportCallback(metrics);
    } else {
      console.log('[Performance Metrics]', metrics);
    }
  };
  
  try {
    // LCP Observer
    const lcpObserver = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      const lastEntry = entries[entries.length - 1];
      metrics.lcp = lastEntry.startTime;
      
      // Report LCP as soon as it's available
      if (metrics.lcp) {
        console.log(`[Performance] LCP: ${Math.round(metrics.lcp)}ms`);
      }
    });
    
    lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
    
    // FID Observer
    const fidObserver = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      const firstEntry = entries[0];
      metrics.fid = firstEntry.processingStart - firstEntry.startTime;
      
      console.log(`[Performance] FID: ${Math.round(metrics.fid)}ms`);
    });
    
    fidObserver.observe({ type: 'first-input', buffered: true });
    
    // CLS Observer
    const clsObserver = new PerformanceObserver((entryList) => {
      for (const entry of entryList.getEntries()) {
        // Only count layout shifts without recent user input
        if (!entry.hadRecentInput) {
          metrics.cls += entry.value;
        }
      }
      
      console.log(`[Performance] Current CLS: ${metrics.cls.toFixed(3)}`);
    });
    
    clsObserver.observe({ type: 'layout-shift', buffered: true });
    
    // Report final metrics when the page is being unloaded
    window.addEventListener('beforeunload', reportMetrics);
    
    // Also report metrics when the page becomes hidden (user navigates away)
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          reportMetrics();
        }
      });
    }
    
  } catch (e) {
    console.error('Error setting up performance monitoring:', e);
  }
};

/**
 * Get network connection information
 * @returns {Object} Connection info with type and saveData status
 */
export const getConnectionInfo = () => {
  if (typeof navigator === 'undefined' || !navigator.connection) {
    return { type: 'unknown', saveData: false };
  }
  
  return {
    type: navigator.connection.effectiveType || 'unknown',
    saveData: !!navigator.connection.saveData,
    downlink: navigator.connection.downlink
  };
};

/**
 * Check if the current device is a mobile device
 * @returns {boolean} True if mobile device
 */
export const isMobileDevice = () => {
  if (typeof navigator === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

/**
 * Measure time taken to execute a function
 * @param {Function} fn - Function to measure
 * @param {Array} args - Arguments to pass to the function
 * @returns {Object} Result and timing information
 */
export const measureExecution = (fn, ...args) => {
  if (typeof performance === 'undefined') {
    // Fallback for SSR
    const result = fn(...args);
    return {
      result,
      time: 0,
      timestamp: Date.now()
    };
  }
  
  const start = performance.now();
  const result = fn(...args);
  const end = performance.now();
  
  return {
    result,
    time: end - start,
    timestamp: Date.now()
  };
};